/**
 * 底部统计行（浮层 DOM 模拟）
 *
 * 启用条件：options.summaryRow.enable === true 且 options.summaryRow.columns 非空
 * 二者缺一不可 —— 不指定列视为不处理统计行
 *
 * sheet 范围：options.summaryRow.sheets
 *   - null / []  => 所有 sheet 都显示统计行（默认）
 *   - [0, 2]     => 仅在这些 index 的 sheet 上显示，切换到其他 sheet 自动隐藏
 *
 * 实现：在 #luckysheet-cell-main 内部贴底叠加一个绝对定位的浮层，
 *      按列宽对齐渲染指定列的 SUM，横向滚动跟随，数据变化自动刷新。
 */
import Store from "../store";
import luckysheetConfigsetting from "./luckysheetConfigsetting";

let $summaryBar = null;       // 浮层根节点
let $inner = null;            // 内容容器（跟随横向滚动）
let scrollHandler = null;
let resizeHandler = null;
let wrappedHooks = [];        // 已包装的 hook（用于销毁时还原）
let renderScheduled = false;

// 判断是否启用：enable=true 且 columns 非空
function isEnabled() {
    const cfg = luckysheetConfigsetting.summaryRow;
    return !!(cfg && cfg.enable && Array.isArray(cfg.columns) && cfg.columns.length > 0);
}

// 判断当前 sheet 是否在配置范围内
// sheets 为 null/空数组 => 所有 sheet 都启用；否则按 index 匹配
// 注意：sheet 的 index 字段可能是数字(0) 也可能是字符串("0")，必须宽松比较
// 否则 sheets:[0] 匹配不上 currentSheetIndex:"0"，浮层会被一直 hide()
function isCurrentSheetEnabled() {
    const cfg = luckysheetConfigsetting.summaryRow;
    if (!cfg.sheets || !Array.isArray(cfg.sheets) || cfg.sheets.length === 0) {
        return true;
    }
    const cur = Store.currentSheetIndex;
    return cfg.sheets.some(function (s) { return s == cur; });
}

// 计算指定列的合计（仅统计显示出来的行，跳过被筛选隐藏的行）
function computeColSum(colIndex) {
    const data = Store.flowdata;
    if (!data || !data.length) return 0;
    const rowhidden = (Store.config && Store.config["rowhidden"]) || null;
    let sum = 0;
    for (let r = 0; r < data.length; r++) {
        // 筛选隐藏的行不参与合计（rowhidden 的 key 是原始行号，与 flowdata 索引一致）
        if (rowhidden && rowhidden[r] != null) continue;
        const cell = data[r] && data[r][colIndex];
        if (!cell) continue;
        const v = cell.v;
        if (typeof v === "number") {
            sum += v;
        } else if (typeof v === "string" && v.trim() !== "" && !isNaN(Number(v))) {
            sum += Number(v);
        }
    }
    return sum;
}

// 简单数字格式化（千分位，最多 4 位小数）
function formatNumber(n) {
    if (!isFinite(n)) return "";
    const rounded = Number.isInteger(n) ? n : parseFloat(n.toFixed(4));
    return rounded.toLocaleString();
}

// 主渲染逻辑
function renderSummaryRow() {
    if (!isEnabled() || !$summaryBar || !$summaryBar.length) return;

    // 防御：浮层若脱离了 DOM，重新挂回不滚动的宿主
    if (!$.contains(document, $summaryBar[0])) {
        $("#luckysheet-grid-window-1").append($summaryBar);
    }

    // 当前 sheet 不在配置范围内则隐藏浮层
    if (!isCurrentSheetEnabled()) {
        $summaryBar.hide();
        return;
    }
    $summaryBar.show();

    const cfg = luckysheetConfigsetting.summaryRow;
    const visibleCols = Store.visibledatacolumn;
    if (!visibleCols || !visibleCols.length) return;

    const scrollBarSize = Store.cellMainSrollBarSize || 12;
    // Luckysheet 用自定义滚动条，cell-main 是 overflow:hidden，横向偏移要从 scrollbar-x 读
    const scrollLeft = $("#luckysheet-scrollbar-x").scrollLeft() || 0;
    const height = cfg.height || 24;

    // 浮层宿主是不滚动的 #luckysheet-grid-window-1（不能用 cell-main —— 它虽 overflow:hidden
    // 但 Luckysheet 会把 scrollbar 的 scrollTop/scrollLeft 同步到 cell-main，让 absolute 子元素跟随滚动）
    // 用 cell-main 的实时 boundingRect 相对 grid-window-1 计算贴底位置
    const cmEl = document.getElementById("luckysheet-cell-main");
    const gwEl = document.getElementById("luckysheet-grid-window-1");
    const cmRect = cmEl ? cmEl.getBoundingClientRect() : null;
    const gwRect = gwEl ? gwEl.getBoundingClientRect() : null;
    if (!cmRect || !gwRect) return;

    // 防御：cell-main 尺寸未就绪（luckysheetsizeauto 可能还没执行，
    // 此时 cmRect.width 是初始 CSS 值，通常 < 100）。用 rAF 延后重试，
    // 直到 luckysheetsizeauto 设好 cell-main 尺寸；之后任何事件也会触发刷新
    if (cmRect.width < 100) {
        scheduleRender();
        return;
    }

    const cmLeftInGw = cmRect.left - gwRect.left;     // cell-main 左边相对宿主的偏移（= 行表头宽）
    const cmBottomInGw = cmRect.bottom - gwRect.top;  // cell-main 底边相对宿主顶部的距离

    // 浮层定位：贴 cell-main 底部；cmLeftInGw 已是行表头宽，无需再叠加
    // 右侧留出垂直滚动条
    $summaryBar.css({
        position: "absolute",
        left: cmLeftInGw,
        top: cmBottomInGw - scrollBarSize - height,
        width: cmRect.width - scrollBarSize,
        height: height + "px",
        background: "#739cd4",
        borderTop: "1px solid #d0d0d0",
        overflow: "hidden",
        zIndex: 9999,
        pointerEvents: "none",   // 不拦截鼠标事件
    });

    // 内容容器跟随横向滚动
    $inner.css("transform", "translateX(" + -scrollLeft + "px)");
    $inner.empty();

    // 为每个指定列生成格子（绝对定位对齐表格列）
    cfg.columns.forEach(function (c) {
        if (c < 0 || c >= visibleCols.length) return;
        const left = c === 0 ? 0 : visibleCols[c - 1];
        const w = c === 0 ? visibleCols[0] : (visibleCols[c] - visibleCols[c - 1]);
        const sum = computeColSum(c);
        const text =
            typeof cfg.formatter === "function"
                ? cfg.formatter(sum, c)
                : sum === 0 ? "" : formatNumber(sum);
        $(
            '<div class="summary-cell" style="position:absolute;left:' + left + "px;width:" + w +
            "px;height:100%;padding:0 4px;line-height:" + height + "px;font-size:12px;" +
            "font-weight:bold;text-align:right;color:#000000;" +
            "border-right:1px solid #e0e0e0;box-sizing:border-box;white-space:nowrap;" +
            'overflow:hidden;text-overflow:ellipsis;">' + text + "</div>"
        ).appendTo($inner);
    });
}

// 用 rAF 节流刷新（高频 hook 触发时合并为一帧一次）
function scheduleRender() {
    if (renderScheduled) return;
    renderScheduled = true;
    requestAnimationFrame(function () {
        renderScheduled = false;
        renderSummaryRow();
    });
}

// 包装 hook：在用户原回调之后触发刷新，不破坏用户逻辑
function wrapHook(hookName) {
    const hook = luckysheetConfigsetting.hook || (luckysheetConfigsetting.hook = {});
    const original = hook[hookName];
    hook[hookName] = function () {
        let ret;
        if (typeof original === "function") {
            ret = original.apply(this, arguments);
        }
        scheduleRender();
        return ret;
    };
    wrappedHooks.push({ name: hookName, original: original });
}

// 初始化（在 initialWorkBook 末尾调用）
function initialSummaryRow() {
    if (!isEnabled()) return;   // 不指定列则完全不处理

    destroySummaryRow();

    // 创建浮层 DOM（挂在不滚动的 grid-window-1，而非 cell-main）
    $summaryBar = $('<div id="luckysheet-summary-row"></div>');
    $inner = $('<div class="summary-inner" style="position:relative;height:100%;"></div>');
    $summaryBar.append($inner);
    $("#luckysheet-grid-window-1").append($summaryBar);

    // 首次渲染尝试：visibledatacolumn 此刻多半还没就绪（在 initialjfFile 的异步链里），
    // 不就绪会静默 return；真正的首次渲染由下方包装的 workbookCreateAfter hook 触发
    // （该 hook 在 setSheetParam 设好 visibledatacolumn/flowdata 之后才触发）
    renderSummaryRow();

    // 监听滚动条（Luckysheet 用自定义滚动条，cell-main 是 overflow:hidden 不滚动）
    scrollHandler = function () { scheduleRender(); };
    $("#luckysheet-scrollbar-x, #luckysheet-scrollbar-y").on("scroll.summaryRow", scrollHandler);

    // 监听窗口 resize（列宽/容器变化）
    resizeHandler = function () { scheduleRender(); };
    $(window).on("resize.summaryRow", resizeHandler);

    // 拦截 hook 自动刷新（数据变化、初始加载、切换 sheet）
    wrapHook("updated");              // 任何进撤销栈的编辑
    wrapHook("workbookCreateAfter");  // 初始数据加载完成
    wrapHook("sheetActivate");        // 切换工作表
}

// 销毁（destroy 时调用）
function destroySummaryRow() {
    if (scrollHandler) {
        $("#luckysheet-scrollbar-x, #luckysheet-scrollbar-y").off("scroll.summaryRow", scrollHandler);
        scrollHandler = null;
    }
    if (resizeHandler) {
        $(window).off("resize.summaryRow", resizeHandler);
        resizeHandler = null;
    }
    // 还原 hook
    if (wrappedHooks.length) {
        const hook = luckysheetConfigsetting.hook || {};
        wrappedHooks.forEach(function (item) {
            if (item.original === undefined) {
                delete hook[item.name];
            } else {
                hook[item.name] = item.original;
            }
        });
        wrappedHooks = [];
    }
    if ($summaryBar) {
        $summaryBar.remove();
        $summaryBar = null;
    }
    $inner = null;
}

export { initialSummaryRow, renderSummaryRow, destroySummaryRow };
